import { IconButton } from '@material-ui/core';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import React, { useEffect, useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { appreciationActions, discussionActions, infoActions, messageActions, newspinActions, quoteActions, socialMediaActions, spaceActions, usercontainerActions } from '../actions';
import { containerLanguage } from '../constants';
import { newsswitchConstants } from '../constants/newsswitch.constants';
import { Spinner } from '../spinner/spinner';
import { CustomSavedNewsPagination } from './CustomSavedNewsPagination';
import { ButtonSpinner, CustomPagination } from './CustomPagination';
import { DraggableDialogForConfirmUnsave, SocialMediaNews, SocialMediaNewsPreview } from './Item';
import { DraggableLoadingPopup } from './SpaceItem';


export const SocialMediaListRender = ({
    stateList,
    socialmediaList,
    setSocialmedialList,
    loading,
    errorState,
    error,
    setError,
    doneState,
    done,
    setDone,
    dataLimitPerPage
}) => {

    const t = useSelector(state => state.themeswitch);
    const [theme, setTheme] = useState(t);
    const dispatch = useDispatch();
    const pw = useSelector(state => state.previewswitch);
    const [previewswitch, setPreviewSwitch] = useState(pw);

    const lin = useSelector((state) => state.authentication.loggedIn);
    const u = useSelector((state) => state.authentication.user);
    const authenticated = lin && u;

    const newsSwitch = useSelector(state => state.newsswitch);
    const pinCounts = useSelector(state => state.newspin.pinCounts);
    // Gates the Saved News empty-state message the same way the ALL_NEWS
    // branch below gates its own loading state - only relevant while
    // savedNewsList is still empty (see the ternary further down); once the
    // list has items they're always shown, so a later unrelated pin/unpin
    // flipping this shared flag can't blank an already-populated view.
    const newspinLoading = useSelector(state => state.newspin.loading);

    // Local copy so an unsave can remove the row immediately (see
    // handleConfirmUnsave below), instead of waiting on the pin dispatch to
    // resolve and Redux to re-render - same optimistic-first pattern as
    // Publisher.js/SpaceItem.js/NewsSaveButton. No rollback on failure,
    // matching those precedents. Reconciled whenever Redux's own value
    // changes (the dispatch landing, another tab, or the mount-time fetch).
    const savedNewsListFromRedux = useSelector(state => state.newspin.savedNewsList);
    const [savedNewsList, setSavedNewsList] = useState(savedNewsListFromRedux);

    useEffect(() => {
        setSavedNewsList(savedNewsListFromRedux);
    }, [savedNewsListFromRedux]);

    // The user's own card list (the 20 built-in PLATFORM entries plus any
    // THEME card they created) and its order. Only authenticated users have
    // one: an anonymous visitor issues no extra request at all and renders
    // from /service/news exactly as before.
    const lang = useSelector(state => state.languageswitch);
    const containers = useSelector(state => state.usercontainer.containers);
    const containersLoaded = useSelector(state => state.usercontainer.loaded);

    // Keyed on the uuid rather than on `authenticated` (which is the user
    // OBJECT): same gate, but a new user object carrying the same identity
    // cannot trigger a redundant re-fetch. undefined whenever logged out,
    // which is what stops the request.
    const containerUuid = authenticated ? u.uuid : undefined;

    // Re-fetched on a language switch: a THEME belongs to exactly one language
    // and does not appear in the others, so the list is language-specific.
    useEffect(() => {
        if (containerUuid) {
            dispatch(usercontainerActions.getMyContainers(containerUuid, containerLanguage(lang)));
        }
    }, [containerUuid, lang, dispatch]);

    // Primitive (not the containers array) so the memo below keeps a stable
    // dependency. `loaded` is reset on LOGOUT, so it is only ever true for the
    // user it was fetched for.
    const useContainers = Boolean(authenticated) && containersLoaded;

    // The card list actually rendered: `containers` is the spine (it carries
    // the user's order and their THEME cards), /service/news supplies the
    // articles for the PLATFORM entries.
    //
    // Returning the IDENTICAL socialmediaList.socialmedias reference whenever
    // there is nothing to merge is load-bearing, not hygiene: CustomPagination
    // resets to page 1 on every new `data` reference, so a fresh array each
    // render would pin the user to page 1 permanently.
    const mergedSocialmedias = useMemo(() => {
        const news = socialmediaList && socialmediaList.socialmedias;
        if (!news || !useContainers || !containers || containers.length === 0) {
            return news;
        }

        const merged = [];
        const matched = new Set();

        // Iterate the containers, never the news array, so the user's order is
        // what gets rendered. `position` is presentation order only - it is the
        // dense index of the language-filtered list, so the same stored row
        // reports a different position in a different language. Array order is
        // authoritative: it is never sorted on, persisted or keyed off.
        containers.forEach(c => {
            if (c.kind === "THEME") {
                // Already carries news/description/website/linkspreview, and
                // last_update is the same Java Date.toString() format the
                // platform cards use - passed straight through, never parsed.
                merged.push(c);
                return;
            }
            if (c.kind === "PLATFORM") {
                // Exact-case match: /mycontainers and /service/news return
                // byte-identical platform names.
                const match = news.find(s => s.name === c.name);
                if (match) {
                    merged.push({ ...match, kind: "PLATFORM" });
                    matched.add(c.name);
                }
                // else: the feed job has not produced this platform yet, so
                // skip it - a card header with no articles is worse than its
                // absence.
            }
        });

        // Anything /service/news returned that matched no container, in its
        // original order, so a platform can never silently disappear.
        news.forEach(s => {
            if (!matched.has(s.name)) {
                merged.push({ ...s, kind: "PLATFORM" });
            }
        });

        return merged;
    }, [containers, useContainers, socialmediaList]);

    if (window.innerWidth >= 900 && window.innerWidth < 1200) {
        dataLimitPerPage = 15;
    } else if (window.innerWidth >= 1200 && window.innerWidth < 1482) {
        dataLimitPerPage = 14;
    } else if (window.innerWidth >= 1482) {
        dataLimitPerPage = 15;
    }

    const ls = useSelector(state => state.socialmedia.loading);
    const [socialLoading, setSocialLoading] = useState(ls);
    const lses = useSelector(state => state.socialmediaES.loading);
    const [socialLoadingES, setSocialLoadingES] = useState(lses);
    const lsde = useSelector(state => state.socialmediaDE.loading);
    const [socialLoadingDE, setSocialLoadingDE] = useState(lsde);
    const lsfr = useSelector(state => state.socialmediaFR.loading);
    const [socialLoadingFR, setSocialLoadingFR] = useState(lsfr);
    const lspt = useSelector(state => state.socialmediaPT.loading);
    const [socialLoadingPT, setSocialLoadingPT] = useState(lspt);
    const lshi = useSelector(state => state.socialmediaHI.loading);
    const [socialLoadingHI, setSocialLoadingHI] = useState(lshi);
    const lsindo = useSelector(state => state.socialmediaINDO.loading);
    const [socialLoadingINDO, setSocialLoadingINDO] = useState(lsindo);
    const lsru = useSelector(state => state.socialmediaRU.loading);
    const [socialLoadingRU, setSocialLoadingRU] = useState(lsru);
    const lsch = useSelector(state => state.socialmediaCH.loading);
    const [socialLoadingCH, setSocialLoadingCH] = useState(lsch);
    const lsjp = useSelector(state => state.socialmediaJP.loading);
    const [socialLoadingJP, setSocialLoadingJP] = useState(lsjp);
    const lsar = useSelector(state => state.socialmediaAR.loading);
    const [socialLoadingAR, setSocialLoadingAR] = useState(lsar);
    const lsit = useSelector(state => state.socialmediaIT.loading);
    const [socialLoadingIT, setSocialLoadingIT] = useState(lsit);
    const lstr = useSelector(state => state.socialmediaTR.loading);
    const [socialLoadingTR, setSocialLoadingTR] = useState(lstr);
    const lsnl = useSelector(state => state.socialmediaNL.loading);
    const [socialLoadingNL, setSocialLoadingNL] = useState(lsnl);
    const lsgr = useSelector(state => state.socialmediaGR.loading);
    const [socialLoadingGR, setSocialLoadingGR] = useState(lsgr);
    const lmya = useSelector(state => state.myappreciation.loading);
    const [loadingMyAppreciation, setLoadingMyAppreciation] = useState(lmya);
    const lmess = useSelector(state => state.message.loading);
    const [loadingMessage, setLoadingMessage] = useState(lmess);
    const linf = useSelector(state => state.info.loading);
    const [loadingInfo, setLoadingInfo] = useState(linf);
    const lq = useSelector(state => state.quote.loading);
    const [loadingQuote, setLoadingQuote] = useState(lq);

    const [openLoadingPopup, setOpenLoadingPopup] = useState(false);
    const handleOpenLoadingPopup = (e) => {
        setOpenLoadingPopup(true);
    }
    const handleCloseLoadingPopup = () => {
        setOpenLoadingPopup(false);
    }

    useEffect(() => {
        setSocialmedialList(stateList);
    }, [stateList]);

    useEffect(() => {
        setError(errorState);
    }, [errorState]);

    useEffect(() => {
        setDone(doneState);
    }, [doneState]);

    useEffect(() => {
        setTheme(t);
    }, [t])

    useEffect(() => {
        localStorage.setItem("preview", pw)
        setPreviewSwitch(pw);
    }, [pw]);


    useEffect(() => {
        setSocialLoading(ls);
    }, [ls]);

    useEffect(() => {
        setSocialLoadingES(lses);
    }, [lses]);

    useEffect(() => {
        setSocialLoadingDE(lsde);
    }, [lsde]);

    useEffect(() => {
        setSocialLoadingFR(lsfr);
    }, [lsfr]);

    useEffect(() => {
        setSocialLoadingPT(lspt);
    }, [lspt]);

    useEffect(() => {
        setSocialLoadingHI(lshi);
    }, [lshi]);

    useEffect(() => {
        setSocialLoadingINDO(lsindo);
    }, [lsindo]);

    useEffect(() => {
        setSocialLoadingRU(lsru);
    }, [lsru]);

    useEffect(() => {
        setSocialLoadingCH(lsch);
    }, [lsch]);

    useEffect(() => {
        setSocialLoadingJP(lsjp);
    }, [lsjp]);

    useEffect(() => {
        setSocialLoadingAR(lsar);
    }, [lsar]);

    useEffect(() => {
        setSocialLoadingIT(lsit);
    }, [lsit]);

    useEffect(() => {
        setSocialLoadingTR(lstr);
    }, [lstr]);

    useEffect(() => {
        setSocialLoadingNL(lsnl);
    }, [lsnl]);

    useEffect(() => {
        setSocialLoadingGR(lsgr);
    }, [lsgr]);

    useEffect(() => {
        setLoadingMyAppreciation(lmya);
    }, [lmya]);

    useEffect(() => {
        setLoadingMessage(lmess);
    }, [lmess]);

    useEffect(() => {
        setLoadingInfo(linf);
    }, [linf]);

    useEffect(() => {
        setLoadingQuote(lq);
    }, [lq]);

    const getPageLimit = (allData, dataLimitPerPage) => {
        if (allData % dataLimitPerPage === 0) {
            return allData / dataLimitPerPage;
        } else {
            return Math.floor(allData / dataLimitPerPage) + 1;
        }
    }

    const getAllNews = () => {
        return (dispatch, getState) => {
            batch(() => {
                dispatch(socialMediaActions.getSocialMediaList());
                dispatch(socialMediaActions.getSocialMediaListES());
                dispatch(socialMediaActions.getSocialMediaListDE());
                dispatch(socialMediaActions.getSocialMediaListFR());
                dispatch(socialMediaActions.getSocialMediaListPT());
                dispatch(socialMediaActions.getSocialMediaListHI());
                dispatch(socialMediaActions.getSocialMediaListINDO());
                dispatch(socialMediaActions.getSocialMediaListRU());
                dispatch(socialMediaActions.getSocialMediaListCH());
                dispatch(socialMediaActions.getSocialMediaListJP());
                dispatch(socialMediaActions.getSocialMediaListAR());
                dispatch(socialMediaActions.getSocialMediaListIT());
                dispatch(socialMediaActions.getSocialMediaListTR());
                dispatch(socialMediaActions.getSocialMediaListNL());
                dispatch(socialMediaActions.getSocialMediaListGR());
                dispatch(quoteActions.getQuotes());
            })

        }
    }

    const getAllInfosForSpace = () => {
        return (dispatch, getState) => {
            batch(() => {
                dispatch(discussionActions.getFeedFilter())
                dispatch(discussionActions.getFeed());
                dispatch(spaceActions.getSpaceList());
                dispatch(infoActions.getInfos())
                dispatch(messageActions.checkReadMessage())
                dispatch(appreciationActions.myAppreciation())
            })
        }
    }

    const refresh = () => {
        dispatch(getAllNews())
        dispatch(getAllInfosForSpace())
    }

    const [confirmUnsaveOpen, setConfirmUnsaveOpen] = useState(false);
    const [itemToUnsave, setItemToUnsave] = useState(null);

    const unsaveNews = (item) => {
        setItemToUnsave(item);
        setConfirmUnsaveOpen(true);
    }

    const handleCloseConfirmUnsave = () => {
        setConfirmUnsaveOpen(false);
        setItemToUnsave(null);
    }

    const handleConfirmUnsave = () => {
        if (itemToUnsave) {
            // Optimistic removal - the row disappears immediately, before/
            // independent of the dispatch below resolving. No rollback on
            // failure (matches Publisher.js/SpaceItem.js precedent).
            setSavedNewsList((prev) => (prev || []).filter(i => i.url !== itemToUnsave.url));

            // itemToUnsave comes from the saved-list response (InfoNews-shaped:
            // url/title/source/...) and carries no user identity fields - the
            // backend looks the user up by email, so those must come from the
            // authenticated user (u), not the item itself.
            var pinInfos = {
                ...itemToUnsave,
                pinned: false,
                uuid: u.uuid,
                firstname: u.firstname,
                name: u.name,
                email: u.email,
                displayname: u.displayname
            };
            dispatch(newspinActions.pin(pinInfos))
        }
        setConfirmUnsaveOpen(false);
        setItemToUnsave(null);
    }

    if (newsSwitch === newsswitchConstants.SAVED_NEWS_NR) {
        return (
            <div class="col-12 col-md-10" style={{ marginTop: "10px" }}>
                <div class="marginMobil" style={{ marginRight: "30px", marginLeft: "200px" }}>
                    <div style={{ paddingTop: 30, paddingBottom: 30 }}>
                        <h1 style={{ color: theme === 'dark' ? '#fff' : '#000' }}>Social News</h1>
                    </div>
                    {savedNewsList && savedNewsList.length > 0 ?
                        <CustomSavedNewsPagination data={savedNewsList} theme={theme} pinCounts={pinCounts} onUnsave={unsaveNews} /> :
                        (newspinLoading ?
                            <Spinner show={newspinLoading} /> :
                            <h2 style={{ borderRadius: 20, marginTop: 32, maxWidth: 1020, color: '#fff' }} class="p-3 mb-2 bg-primary bg-gradient text-white">You haven't saved any news yet.</h2>)
                    }
                </div>
                <DraggableDialogForConfirmUnsave open={confirmUnsaveOpen} handleClose={handleCloseConfirmUnsave} handleConfirm={handleConfirmUnsave} />
            </div>
        );
    }

    if (!socialmediaList || !socialmediaList.socialmedias) {
        if (loading) {
            return (
                <>
                    <div class="col-12 col-md-10" style={{ marginTop: "10px" }}>
                        <div class="marginMobil" style={{ marginRight: "30px", marginLeft: "200px" }}>
                            <div>
                                <div style={{ paddingTop: 30, paddingBottom: 30 }}>
                                    <Spinner show={loading} />
                                    <h1 style={{ color: theme === 'dark' ? '#fff' : '#000' }} >Social News</h1> {/* light */}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            );
        }
        if (done) {
            if (error) {
                return (
                    <>
                        <div class="col-12 col-md-10" style={{ marginTop: "10px" }}>
                            <div class="marginMobil" style={{ marginRight: "30px", marginLeft: "200px" }}>
                                <div>
                                    <div style={{ paddingTop: 30, paddingBottom: 30 }}>
                                        <h1 style={{ color: theme === 'dark' ? '#fff' : '#000' }} >Social News</h1> {/* light */}
                                        <h2 style={{ borderRadius: 20, marginTop: 32, maxWidth: 1020, color: '#fff' }} class="p-3 mb-2 bg-primary bg-gradient text-white"><WarningIcon style={{ marginBottom: 5 }} />{" "}This could be a network issue or the App is in maintenance mode. Please refresh the page{" "}<IconButton style={{ color: "#fff" }} onClick={() => refresh()} title="Load News"><RefreshIcon /></IconButton>{" "}or try again later.</h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                );
            }
        }
        return (
            <>
                <div class="col-12 col-md-10" style={{ marginTop: "10px" }}>
                    <div class="marginMobil" style={{ marginRight: "30px", marginLeft: "200px" }}>
                        <div>
                            <div style={{ paddingTop: 30, paddingBottom: 30 }}>
                                <h1 style={{ color: theme === 'dark' ? '#fff' : '#000' }} >
                                    Social News {<ButtonSpinner handleOpenLoadingPopup={handleOpenLoadingPopup} show={
                                        socialLoading ||
                                        socialLoadingES ||
                                        socialLoadingDE ||
                                        socialLoadingFR ||
                                        socialLoadingPT ||
                                        socialLoadingHI ||
                                        socialLoadingINDO ||
                                        socialLoadingRU ||
                                        socialLoadingCH ||
                                        socialLoadingJP ||
                                        socialLoadingAR ||
                                        socialLoadingIT ||
                                        socialLoadingTR ||
                                        socialLoadingNL ||
                                        socialLoadingGR ||
                                        loadingMyAppreciation ||
                                        loadingMessage ||
                                        loadingInfo ||
                                        loadingQuote
                                    } />}
                                </h1>

                                <DraggableLoadingPopup openLoadingPopup={openLoadingPopup} handleCloseLoadingPopup={handleCloseLoadingPopup} />
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
    else {
        return (
            <>
                {mergedSocialmedias.length > 0 ? (
                    <>
                        <CustomPagination
                            data={mergedSocialmedias}
                            RenderComponent={previewswitch ? SocialMediaNewsPreview : SocialMediaNews}
                            title="Social News"
                            pageLimit={getPageLimit(mergedSocialmedias.length, dataLimitPerPage)}
                            dataLimit={dataLimitPerPage}
                        />
                    </>
                ) : (
                    <>
                        <div class="col-12 col-md-10" style={{ marginTop: "10px" }}>
                            <div class="marginMobil" style={{ marginRight: "30px", marginLeft: "200px" }}>
                                <div>
                                    <div style={{ paddingTop: 30, paddingBottom: 30 }}>
                                        <h1 style={{ color: theme === 'dark' ? '#fff' : '#000' }} >Social News</h1> {/* light */}
                                        <h2 style={{ borderRadius: 20, marginTop: 32, maxWidth: 1020, color: '#fff' }} class="p-3 mb-2 bg-primary bg-gradient text-white"><WarningIcon style={{ marginBottom: 5 }} />{" "}This could be a network issue or the App is in maintenance mode. Please refresh the page{" "}<IconButton style={{ color: "#fff" }} onClick={() => refresh()} title="Load News"><RefreshIcon /></IconButton>{" "}or try again later...</h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </>
        );
    }
}